/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import { useEffect, useState, useRef, useCallback } from 'react';

import Highcharts from 'highcharts';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsReact from 'highcharts-react-official';
import wordcloud from 'highcharts/modules/wordcloud';
import SlideBackground from '@atoms/new/slideBackground';
import ResultHeader from '@components/analytics/ResultHeader';

if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts);
  wordcloud(Highcharts);
  // eslint-disable-next-line func-names
  Highcharts.seriesTypes.wordcloud.prototype.deriveFontSize = function (relativeWeight) {
    const maxFontSize = 1 / 7;

    if (relativeWeight >= 0 && relativeWeight < maxFontSize) {
      return 5;
    }
    if (relativeWeight >= maxFontSize && relativeWeight < 2 * maxFontSize) {
      return 10;
    }
    if (relativeWeight >= 2 * maxFontSize && relativeWeight < 3 * maxFontSize) {
      return 15;
    }
    if (relativeWeight >= 3 * maxFontSize && relativeWeight < 4 * maxFontSize) {
      return 20;
    }
    if (relativeWeight >= 4 * maxFontSize && relativeWeight < 5 * maxFontSize) {
      return 25;
    }
    if (relativeWeight >= 5 * maxFontSize && relativeWeight < 6 * maxFontSize) {
      return 30;
    }
    return 30;
  };
}

Highcharts.error = function (code, stop, chart, params) {
  console.warn('Highcharts error:', code, stop, chart, params);
};

const WordChart = ({
  highchartsRef,
  chartOptions,
  isFullScreen,
  splitDiv,
  containerProps,
  backgroundColor,
  bgImg,
  bgImgOpacity,
  colors,
  title,
  type,
  analytics,
  textAnalytics,
  showInstantCustomisationPopup
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [chartKey, setChartKey] = useState(0);
  const internalChartRef = useRef(null);

  const chartRef = highchartsRef || internalChartRef;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const createSafeChartOptions = useCallback(() => {
    const fallbackOptions = {
      chart: {
        backgroundColor: backgroundColor || 'rgba(255, 255, 255, 0.0)',
        height: '100%'
      },
      title: { text: '' },
      credits: { enabled: false },
      exporting: { enabled: false },
      series: []
    };

    try {
      if (hasError || !chartOptions || typeof chartOptions !== 'object') {
        return fallbackOptions;
      }

      return {
        ...chartOptions,
        chart: {
          backgroundColor: backgroundColor || 'rgba(255, 255, 255, 0.0)',
          ...chartOptions.chart
        }
      };
    } catch (error) {
      console.warn('Error creating chart options:', error);
      setHasError(true);
      return fallbackOptions;
    }
  }, [chartOptions, backgroundColor, hasError]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('resize'));
      }
    } catch (error) {
      console.warn('Error dispatching resize event:', error);
    }
  }, [splitDiv, isFullScreen, showInstantCustomisationPopup]);

  useEffect(() => {
    if (hasError) {
      const timer = setTimeout(() => {
        setHasError(false);
        setChartKey(prev => prev + 1);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [hasError]);

  useEffect(() => {
    const handleChartError = (error) => {
      setHasError(true);
    };

    if (chartRef.current?.chart) {
      chartRef.current.chart.container?.addEventListener('error', handleChartError);
    }

    return () => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.container?.removeEventListener('error', handleChartError);
      }
    };
  }, [chartKey, chartRef]);

  if (!isMounted || hasError) {
    return (
      <div
        className={'h-full overflow-hidden w-full'}
        style={{ backgroundColor }}
      >
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#000000'
        }}>
          Loading chart...
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={'h-full overflow-hidden w-full'}
        style={{ backgroundColor }}
        suppressHydrationWarning={true}
      >
        {bgImg && <SlideBackground bgImg={bgImg} bgImgOpacity={bgImgOpacity} />}

        {/* {analytics && <ResultHeader title={title} type={type} textColor={colors?.wcHeadingColor} />} */}

        <HighchartsReact
          key={`wordchart-${chartKey}`}
          highcharts={Highcharts}
          allowChartUpdate={true}
          ref={chartRef}
          options={createSafeChartOptions()}
          containerProps={containerProps}
          updateArgs={[true, true, true]}
          callback={(chart) => {
            try {
              chart.redraw = function () {
                try {
                  Highcharts.Chart.prototype.redraw.apply(this, arguments);
                } catch (error) {
                  console.warn('Chart redraw failed:', error);
                  setHasError(true);
                }
              };
            } catch (error) {
              console.warn('Error setting up chart callback:', error);
              setHasError(true);
            }
          }}
        />
      </div>
    </>
  );
};

export default WordChart;
